import { ImageParams } from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import IconButton from '../Button/IconButton'
import LineAngleLeftIcon from '../Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from '../Icons/line/LineAngleRightIcon'
import LightBox from '../Modal/LightBox'
import BodyText from '../Typography/BodyText'
import BaseImageCarousel, { ImageCarouselRefInterface } from './BaseImageCarousel'
import ImageCarousel from './ImageCarousel'
import { EmptyArray } from 'lib/array/arrayUtils'

const NavigationArrow = styled(IconButton)`
  display: none;
  z-index: 1;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

const GalleryLightBox = styled.div`
  height: 100%;
  display: grid;
  align-items: center;
  position: relative;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 10% 1fr 10%;

    > ${NavigationArrow} {
      align-self: center;
      justify-self: center;
    }
  }

  > [hidden] {
    opacity: 0;
    pointer-events: none;
  }
`

const GalleryReservedRatio = styled.div`
  overflow: hidden;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  position: relative;
  max-height: 100%;
`

const TopBottomTextSection = styled(BodyText)`
  display: flex;
  align-items: start;
  justify-content: center;
  flex-shrink: 0;
  flex-grow: 1;
  padding: 0 ${rem(12)};
`

const GalleryElement = styled.div`
  padding: ${rem(16)} 0;
  display: flex;
  flex-direction: column;
  height: 100vh;
  gap: ${rem(16)};
`

const GalleryWrapper = styled.div`
  overflow: hidden;
`

const galleryImageProps: ImageParams = { mobileAspectRatio: '16:9', quality: 'good', fit: 'center' }

type ImageCarouselProps = Pick<React.ComponentProps<typeof ImageCarousel>, 'images' | 'interstitials' | 'onImageChange' | 'defaultIndex' >

interface Props extends React.PropsWithChildren, ImageCarouselProps {
}

function ImageGalleryLightbox(props: Props) {
  const { images, children, defaultIndex = 0, onImageChange, interstitials = EmptyArray }: Props = props
  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const [currentImage, setCurrentImage] = useState<App.Image | undefined>(images[defaultIndex])
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(defaultIndex)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        carouselRef.current?.previousImage()
      } else if (e.key === 'ArrowRight') {
        carouselRef.current?.nextImage()
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const onArrowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const direction = e.currentTarget.dataset.direction as any
    if (direction === 'left') {
      carouselRef.current?.previousImage()
    } else {
      carouselRef.current?.nextImage()
    }
  }

  const onChangeImage = useCallback((index: number, image?: App.Image) => {
    onImageChange?.(index, image)
    setCurrentImage(image)
    setCurrentImageIndex(index)
  }, [onImageChange])

  return <LightBox>

    <GalleryLightBox>
      <NavigationArrow
        onClick={onArrowClick}
        kind="secondary"
        variant="dark"
        shape="circle"
        size="medium"
        data-direction="left"
        hidden={images.length === 1}
      >
        <LineAngleLeftIcon />
      </NavigationArrow>
      <GalleryElement>
        <TopBottomTextSection variant="medium" colour="neutral-eight" align="center">
          {(currentImageIndex + 1)} / {images.length + interstitials.length}
        </TopBottomTextSection>
        <GalleryWrapper>
          <GalleryReservedRatio>
            <BaseImageCarousel
              defaultIndex={defaultIndex}
              imageParams={galleryImageProps}
              carouselRef={carouselRef}
              images={images}
              onImageChange={onChangeImage}
              interstitials={interstitials}
            />
            {children}
          </GalleryReservedRatio>
        </GalleryWrapper>
        <TopBottomTextSection variant="medium" colour="neutral-eight" align="center">
          {currentImage?.title}&nbsp;
        </TopBottomTextSection>
      </GalleryElement>
      <NavigationArrow
        onClick={onArrowClick}
        className="right"
        variant="dark"
        kind="secondary"
        shape="circle"
        size="medium"
        data-direction="right"
        hidden={images.length === 1}
      >
        <LineAngleRightIcon />
      </NavigationArrow>
    </GalleryLightBox>
  </LightBox>
}

export default ImageGalleryLightbox
